/**
 * Global search composable — manages the search modal state and preview queries.
 */
const _state = reactive({
  isModalOpen: false,
  query: '',
  results: {},
  loading: false
})

let _debounceTimer = null

export function useGlobalSearch() {
  const { locale } = useI18n()

  const results = computed(() => _state.results)
  const loading = computed(() => _state.loading)
  const totalResults = computed(() => Object.values(_state.results).reduce((sum, group) => sum + (group.total || 0), 0))
  const hasResults = computed(() => totalResults.value > 0)

  const isModalOpen = computed({
    get: () => _state.isModalOpen,
    set: (v) => (_state.isModalOpen = v)
  })
  const query = computed({
    get: () => _state.query,
    set: (v) => search(v)
  })

  const resultTypes = computed(() =>
    Object.entries(_state.results)
      .filter(([, group]) => group.items?.length > 0)
      .map(([type, group]) => ({ type, items: group.items, total: group.total }))
  )

  function openModal() {
    _state.isModalOpen = true
  }

  function closeModal() {
    _state.isModalOpen = false
  }

  function search(q) {
    _state.query = q

    if (_debounceTimer) clearTimeout(_debounceTimer)

    if (!q || q.trim().length < 2) {
      _state.results = {}
      _state.loading = false
      return
    }

    _state.loading = true

    _debounceTimer = setTimeout(async () => {
      try {
        const data = await $fetch('/api/search', {
          params: { q: q.trim(), locale: locale.value, limit: 3 }
        })
        // Only apply if the query hasn't changed in the meantime
        if (_state.query === q) {
          _state.results = data.results || {}
        }
      } catch (err) {
        console.error('[useGlobalSearch]', err)
        _state.results = {}
      } finally {
        if (_state.query === q) {
          _state.loading = false
        }
      }
    }, 300)
  }

  function clearSearch() {
    _state.query = ''
    _state.results = {}
    _state.loading = false
    if (_debounceTimer) clearTimeout(_debounceTimer)
  }

  return {
    isModalOpen,
    query,
    results,
    loading,
    totalResults,
    hasResults,
    resultTypes,
    openModal,
    closeModal,
    search,
    clearSearch
  }
}
